import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import { 
  CreditCard, Loader2, Search, Eye, 
  User, Building, Mail, Phone, Calendar, QrCode,
  ExternalLink, RefreshCw, Pencil, RotateCcw
} from 'lucide-react';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminCards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userCard, setUserCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [reactivating, setReactivating] = useState({});

  // Check admin access
  useEffect(() => {
    if (user && !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      toast({
        title: "Access Denied",
        description: "Only admin can access this page",
        variant: "destructive"
      });
      navigate('/');
    }
  }, [user, navigate, toast]);

  // Load user's card for layout
  useEffect(() => {
    const loadUserCard = async () => {
      if (!user) {
        setCardLoading(false);
        return;
      }
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/my-cards`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) setUserCard(data[0]);
        }
      } catch (error) {
        console.error('Error loading user card:', error);
      } finally {
        setCardLoading(false);
      }
    };
    loadUserCard();
  }, [user]);

  // Load all cards
  useEffect(() => {
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      loadCards();
    }
  }, [user]);

  // Filter cards based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCards(cards);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = cards.filter(card => 
      card.name?.toLowerCase().includes(query) ||
      card.username?.toLowerCase().includes(query) ||
      card.email?.toLowerCase().includes(query) ||
      card.company?.toLowerCase().includes(query) ||
      card.title?.toLowerCase().includes(query) ||
      card.phone?.includes(query)
    );
    setFilteredCards(filtered);
  }, [searchQuery, cards]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCards(data);
        setFilteredCards(data);
      } else {
        throw new Error('Failed to load cards');
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      toast({
        title: "Error",
        description: "Failed to load cards",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (username) => {
    if (!window.confirm(`Are you sure you want to reactivate @${username}?`)) {
      return;
    }

    setReactivating(prev => ({ ...prev, [username]: true }));
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cards/admin/${username}/reactivate`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast({
          title: "Card Reactivated",
          description: `@${username} has been restored`
        });
        loadCards();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reactivate card');
      }
    } catch (error) {
      console.error('Error reactivating card:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setReactivating(prev => ({ ...prev, [username]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const activeCards = cards.filter(c => c.isActive !== false);
  const inactiveCards = cards.filter(c => c.isActive === false);
  const nfcLinkedCards = cards.filter(c => c.nfc_code);

  if (!user || !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
            <p className="text-gray-500 mt-2">Only admin can access this page</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-amber-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">All Cards</h1>
                <p className="text-gray-600">View and manage all created business cards</p>
              </div>
            </div>
            <Button
              onClick={loadCards}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-2 border-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Cards</p>
                  <p className="text-2xl font-bold text-gray-900">{cards.length}</p>
                </div>
                <CreditCard className="w-8 h-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Cards</p>
                  <p className="text-2xl font-bold text-green-600">{activeCards.length}</p>
                </div>
                <Eye className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Deleted Cards</p>
                  <p className="text-2xl font-bold text-red-600">{inactiveCards.length}</p>
                </div>
                <CreditCard className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">NFC Linked</p>
                  <p className="text-2xl font-bold text-blue-600">{nfcLinkedCards.length}</p>
                </div>
                <QrCode className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="border-2 border-gray-100 mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, username, email, company, title, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-gray-500 mt-2">
                Showing {filteredCards.length} of {cards.length} cards
              </p>
            )}
          </CardContent>
        </Card>

        {/* Cards List */}
        <Card className="border-2 border-gray-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Cards ({filteredCards.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
              </div>
            ) : filteredCards.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">
                  {searchQuery ? 'No cards match your search' : 'No cards created yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCards.map((card) => (
                  <Card 
                    key={card.id} 
                    className={`border-2 transition-colors ${
                      card.isActive === false 
                        ? 'border-red-100 bg-red-50/50' 
                        : 'border-gray-100 hover:border-amber-200'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Profile Image */}
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden">
                            {card.profileImage ? (
                              <img 
                                src={card.profileImage} 
                                alt={card.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          {/* Card Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-gray-900 truncate">
                                {card.name || 'No Name'}
                              </h3>
                              <Badge variant="outline" className="text-xs">
                                @{card.username}
                              </Badge>
                              {card.isActive === false && (
                                <Badge className="bg-red-600 text-xs">Deleted</Badge>
                              )}
                              {card.nfc_code && (
                                <Badge className="bg-blue-600 text-xs">
                                  <QrCode className="w-3 h-3 mr-1" />
                                  NFC
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                              {card.title && (
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span className="truncate">{card.title}</span>
                                </div>
                              )}
                              {card.company && (
                                <div className="flex items-center gap-1">
                                  <Building className="w-3 h-3" />
                                  <span className="truncate">{card.company}</span>
                                </div>
                              )}
                              {card.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{card.email}</span>
                                </div>
                              )}
                              {card.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span>{card.phone}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>Created: {formatDate(card.createdAt)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                <span>{card.viewCount || 0} views</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/bio/${card.username}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {card.isActive === false ? (
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleReactivate(card.username)}
                              disabled={reactivating[card.username]}
                              data-testid={`reactivate-${card.username}`}
                            >
                              {reactivating[card.username] ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4 mr-1" />
                              )}
                              Reactivate
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700"
                              onClick={() => navigate(`/edit/${card.username}`)}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminCards;
